import {
    sort,
    sortBySortColumn,
    sortOrder,
    ascending,
    descending,
    oldestOnTop,
    newestOnTop,
    largestOnTop,
    smallestOnTop,
    highOnTop,
    lowOnTop,
} from './MailFilterContextMenu.locstring.json';
import loc from 'owa-localize';
import getSortColumnDisplay from '../helpers/getSortColumnDisplay';
import getViewFilterDisplay from '../helpers/getViewFilterDisplay';
import { observer } from 'mobx-react';
import { lazySelectFilter, lazySelectSort } from 'owa-mail-filter-actions';
import {
    getSortByForTable,
    listViewStore,
    MailSortHelper,
    SortColumn,
    TableQueryType,
} from 'owa-mail-list-store';
import { ActionSource } from 'owa-mail-store';
import ViewFilter from 'owa-service/lib/contract/ViewFilter';
import * as React from 'react';
import {
    ContextualMenu,
    DirectionalHint,
    IContextualMenuItem,
    ContextualMenuItemType,
} from 'office-ui-fabric-react/lib/components/ContextualMenu';
import { lazyGetPauseInboxContextMenuItem } from 'owa-mail-pause-inbox';
import { IsShadowMailboxUser } from 'owa-mail-ads-shared/lib/sharedAdsUtils';
import { startSearch, SearchScenarioId } from 'owa-search';

export interface MailListFilterContextMenuProps {
    currentViewFilter: ViewFilter;
    supportedViewFilters: ViewFilter[];
    contextMenuTargetElement: HTMLElement;
    dismissContextMenu: (ev?: any) => void;
    tableViewId: string;
    filterMenuSource?: ActionSource;
}

@observer
export default class MailFilterContextMenu extends React.Component<
    MailListFilterContextMenuProps,
    {}
> {
    // Create listview Filters menu
    private createMenuItems(): IContextualMenuItem[] {
        const filterMenu: IContextualMenuItem[] = this.props.supportedViewFilters
            .filter(
                viewFilter =>
                    !(
                        IsShadowMailboxUser() &&
                        (viewFilter === 'Mentioned' || viewFilter === 'ToOrCcMe')
                    )
            )
            .map((viewFilter: ViewFilter) => {
                return this.getViewFilterMenuItems(viewFilter);
            });

        const tableView = listViewStore.tableViews.get(this.props.tableViewId);
        if (tableView.tableQuery.type != TableQueryType.Search) {
            filterMenu.push({
                key: 'divider',
                name: '-',
            });

            const folderId = listViewStore.tableViews.get(this.props.tableViewId).tableQuery
                .folderId;
            filterMenu.push({
                key: 'sortBy',
                name: loc(sort),
                items: this.getSortMenuItems(),
            });

            const getPauseInboxContextMenuItem = lazyGetPauseInboxContextMenuItem.tryImportForRender();
            if (getPauseInboxContextMenuItem) {
                const pauseMenuItem = getPauseInboxContextMenuItem(
                    folderId,
                    this.props.dismissContextMenu,
                    'filterMenu'
                );
                if (pauseMenuItem) {
                    filterMenu.push({
                        key: 'divider',
                        name: '-',
                    });
                    filterMenu.push(pauseMenuItem);
                }
            }
        }
        return filterMenu;
    }

    private getSortMenuItems(): IContextualMenuItem[] {
        const tableView = listViewStore.tableViews.get(this.props.tableViewId);
        const currentSortBy = getSortByForTable(tableView);
        const isAscending = currentSortBy.sortDirection == MailSortHelper.ASCENDING_SORT_DIR;
        const currentSortColumn = currentSortBy.sortColumn;
        const supportedSortColumns = MailSortHelper.getSupportedSortColumns(
            tableView.tableQuery.folderId
        );
        const sortMenuItems: IContextualMenuItem[] = [];

        const sortByMenuItems: IContextualMenuItem[] = [];
        supportedSortColumns.forEach(sortColumn => {
            const isSelected = currentSortColumn == sortColumn;
            sortByMenuItems.push({
                key: sortColumn.toString(),
                name: getSortColumnDisplay(sortColumn),
                data: {
                    sortOrder: isSelected ? currentSortBy.sortDirection : null,
                    sortColumn: sortColumn,
                },
                onClick: this.onSortSelected,
                canCheck: true,
                isChecked: isSelected,
            });
        });

        sortMenuItems.push({
            key: 'sortBySection',
            itemType: ContextualMenuItemType.Section,
            sectionProps: {
                bottomDivider: true,
                title: loc(sortBySortColumn),
                items: sortByMenuItems,
            },
        });

        const { ascendingString, descendingString } = this.getSortDirectionStrings(
            currentSortColumn
        );
        const sortOrderMenuItems: IContextualMenuItem[] = [
            {
                key: 'ascending',
                name: ascendingString,
                data: { sortOrder: 'Ascending', sortColumn: currentSortColumn },
                onClick: this.onSortSelected,
                canCheck: true,
                isChecked: isAscending,
            },
            {
                key: 'descending',
                name: descendingString,
                data: { sortOrder: 'Descending', sortColumn: currentSortColumn },
                onClick: this.onSortSelected,
                canCheck: true,
                isChecked: !isAscending,
            },
        ];

        sortMenuItems.push({
            key: 'sortOrderSection',
            itemType: ContextualMenuItemType.Section,
            sectionProps: {
                bottomDivider: true,
                title: loc(sortOrder),
                items: sortOrderMenuItems,
            },
        });

        return sortMenuItems;
    }

    private getSortDirectionStrings(sortColumn: SortColumn) {
        switch (sortColumn) {
            case SortColumn.Date:
                return { ascendingString: loc(oldestOnTop), descendingString: loc(newestOnTop) };
            case SortColumn.From:
            case SortColumn.Subject:
                return { ascendingString: loc(ascending), descendingString: loc(descending) };
            case SortColumn.Size:
                return { ascendingString: loc(smallestOnTop), descendingString: loc(largestOnTop) };
            case SortColumn.Importance:
                return { ascendingString: loc(lowOnTop), descendingString: loc(highOnTop) };
            default:
                throw new Error('Sort not implemented');
        }
    }

    // Gets View Filters menu items
    private getViewFilterMenuItems(viewFilter: ViewFilter): IContextualMenuItem {
        return {
            key: viewFilter.toString(),
            name: getViewFilterDisplay(viewFilter),
            data: viewFilter,
            canCheck: true,
            isChecked: this.props.currentViewFilter == viewFilter,
            onClick: this.onFilterSelected,
        };
    }

    private onSortSelected = (
        ev?: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
        item?: IContextualMenuItem
    ) => {
        this.props.dismissContextMenu();
        lazySelectSort.importAndExecute(
            item.data.sortColumn,
            item.data.sortOrder,
            this.props.tableViewId,
            this.props.filterMenuSource
        );
    };

    // Callback when filter menu item is selected
    private onFilterSelected = (
        ev?: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
        item?: IContextualMenuItem
    ) => {
        this.props.dismissContextMenu();

        const tableView = listViewStore.tableViews.get(this.props.tableViewId);
        if (tableView.tableQuery.type == TableQueryType.Search) {
            startSearch(
                'SearchFilterMenu',
                SearchScenarioId.Mail,
                true /* explicitSearch */,
                [],
                undefined,
                item.key
            );
            return;
        } else {
            const viewFilter = item.data as ViewFilter;
            lazySelectFilter.importAndExecute(viewFilter, this.props.filterMenuSource);
        }
    };

    render() {
        const { dismissContextMenu, contextMenuTargetElement } = this.props;
        const items: IContextualMenuItem[] = this.createMenuItems();
        return (
            <ContextualMenu
                shouldFocusOnMount={true}
                target={contextMenuTargetElement}
                directionalHint={DirectionalHint.bottomRightEdge}
                onDismiss={dismissContextMenu}
                items={items}
            />
        );
    }
}
